import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import type {
  AuditListResponse,
  AuditLogRow,
  AuditSource,
} from "../../lib/api-types.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import { auditPath } from "../../lib/api-paths.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { Empty } from "../../components/Empty.js";
import { Drawer } from "../../components/Drawer.js";
import { Table, type ColumnDef } from "../../components/Table.js";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextInput,
} from "../../components/FormField.js";
import { formatDateTime } from "../../lib/format.js";
import { cn } from "../../lib/cn.js";
import type { OrgDetailContext } from "./context.js";

interface FilterState {
  action: string;
  source: AuditSource | "";
  actor: string;
  target: string;
  fromAt: string;
  toAt: string;
}

const PAGE_SIZE = 50;

const INITIAL: FilterState = {
  action: "",
  source: "",
  actor: "",
  target: "",
  fromAt: "",
  toAt: "",
};

const FAILED_ACTIONS = new Set<string>([
  "password-lazy-migrate-write-failed",
  "feishu-token-fetch-failed",
  "signature-invalid",
  "unsupported-event",
]);

export function AuditPanel(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const [draft, setDraft] = useState<FilterState>(INITIAL);
  const [applied, setApplied] = useState<FilterState>(INITIAL);
  const [page, setPage] = useState<number>(1);
  const [detail, setDetail] = useState<AuditLogRow | null>(null);

  const qs = new URLSearchParams();
  if (applied.action) qs.set("action", applied.action);
  if (applied.source) qs.set("source", applied.source);
  if (applied.actor) qs.set("actor", applied.actor);
  if (applied.target) qs.set("target", applied.target);
  if (applied.fromAt) qs.set("fromAt", applied.fromAt);
  if (applied.toAt) qs.set("toAt", applied.toAt);
  qs.set("page", String(page));
  qs.set("pageSize", String(PAGE_SIZE));
  const path = `${auditPath(org.code)}?${qs.toString()}`;
  const { data, loading, error } = useApiQuery<AuditListResponse>(path, [path]);

  const handleApply = (): void => {
    setApplied(draft);
    setPage(1);
  };

  const handleReset = (): void => {
    setDraft(INITIAL);
    setApplied(INITIAL);
    setPage(1);
  };

  const columns: ColumnDef<AuditLogRow>[] = [
    {
      header: "时间",
      width: "w-40",
      render: (r) => (
        <span className="text-xs text-dosia-text-secondary">
          {formatDateTime(r.at)}
        </span>
      ),
    },
    {
      header: "执行人",
      width: "w-32",
      cellClassName: "font-mono text-xs",
      render: (r) => r.actor,
    },
    {
      header: "动作",
      render: (r) => (
        <span
          className={cn(
            "text-xs font-mono",
            FAILED_ACTIONS.has(r.action) &&
              "text-amber-700 rounded-md bg-amber-500/15 px-1",
          )}
        >
          {r.action}
        </span>
      ),
    },
    {
      header: "来源",
      width: "w-20",
      render: (r) => (
        <span className="text-xs text-dosia-text-secondary">{r.source}</span>
      ),
    },
    {
      header: "目标",
      cellClassName: "font-mono text-xs",
      render: (r) =>
        r.target ?? <span className="text-dosia-text-dim">—</span>,
    },
    {
      header: "",
      width: "w-16",
      render: (r) => (
        <button
          type="button"
          className="text-xs text-dosia-accent hover:underline"
          onClick={() => setDetail(r)}
        >
          详情
        </button>
      ),
    },
  ];

  const totalPages = data
    ? Math.max(1, Math.ceil(data.total / data.pageSize))
    : 1;

  return (
    <div className="flex flex-col gap-3">
      <div className="bg-dosia-bg-light border border-dosia-border rounded-md p-3 flex flex-wrap gap-2 items-end">
        <FormField label="动作">
          <TextInput
            value={draft.action}
            onChange={(e) => setDraft({ ...draft, action: e.target.value })}
            placeholder="e.g. user-create"
            className="w-44"
          />
        </FormField>
        <FormField label="来源">
          <Select
            value={draft.source}
            onChange={(e) =>
              setDraft({ ...draft, source: e.target.value as AuditSource | "" })
            }
            className="w-32"
          >
            <option value="">全部</option>
            <option value="ui">ui</option>
            <option value="api">api</option>
            <option value="webhook">webhook</option>
            <option value="sync">sync</option>
            <option value="agent">agent</option>
          </Select>
        </FormField>
        <FormField label="执行人">
          <TextInput
            value={draft.actor}
            onChange={(e) => setDraft({ ...draft, actor: e.target.value })}
            placeholder="root / system"
            className="w-32"
          />
        </FormField>
        <FormField label="目标">
          <TextInput
            value={draft.target}
            onChange={(e) => setDraft({ ...draft, target: e.target.value })}
            placeholder="userId / roleCode"
            className="w-44"
          />
        </FormField>
        <FormField label="从">
          <TextInput
            type="date"
            value={draft.fromAt}
            onChange={(e) => setDraft({ ...draft, fromAt: e.target.value })}
            className="w-36"
          />
        </FormField>
        <FormField label="到">
          <TextInput
            type="date"
            value={draft.toAt}
            onChange={(e) => setDraft({ ...draft, toAt: e.target.value })}
            className="w-36"
          />
        </FormField>
        <div className="flex gap-2 ml-auto">
          <SecondaryButton onClick={handleReset}>重置</SecondaryButton>
          <PrimaryButton onClick={handleApply}>查询</PrimaryButton>
        </div>
      </div>

      {error ? <ErrorBanner>{error.message}</ErrorBanner> : null}
      <Table<AuditLogRow>
        columns={columns}
        rows={data?.rows ?? []}
        rowKey={(r) => r.id}
        loading={loading}
        empty={<Empty title="尚无审计记录" />}
      />
      {data ? (
        <div className="flex items-center justify-between text-xs text-dosia-text-secondary">
          <span>
            共 {data.total} 条 · 第 {data.page} / {totalPages} 页
          </span>
          <div className="flex gap-2">
            <SecondaryButton
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              上一页
            </SecondaryButton>
            <SecondaryButton
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              下一页
            </SecondaryButton>
          </div>
        </div>
      ) : null}

      {detail ? (
        <Drawer
          open
          onClose={() => setDetail(null)}
          title={`审计详情 - ${detail.action}`}
          width={520}
        >
          <dl className="grid grid-cols-[80px_1fr] gap-y-1 text-sm">
            <dt className="text-dosia-text-secondary">时间</dt>
            <dd>{formatDateTime(detail.at)}</dd>
            <dt className="text-dosia-text-secondary">执行人</dt>
            <dd className="font-mono">{detail.actor}</dd>
            <dt className="text-dosia-text-secondary">来源</dt>
            <dd>{detail.source}</dd>
            <dt className="text-dosia-text-secondary">目标</dt>
            <dd className="font-mono">{detail.target ?? "—"}</dd>
          </dl>
          <div className="mt-4">
            <p className="text-xs text-dosia-text-secondary mb-1">Before</p>
            <pre className="font-mono text-xs bg-dosia-bg-input border border-dosia-border rounded-md p-2 overflow-auto">
              {detail.before ? JSON.stringify(detail.before, null, 2) : "—"}
            </pre>
          </div>
          <div className="mt-3">
            <p className="text-xs text-dosia-text-secondary mb-1">After</p>
            <pre className="font-mono text-xs bg-dosia-bg-input border border-dosia-border rounded-md p-2 overflow-auto">
              {detail.after ? JSON.stringify(detail.after, null, 2) : "—"}
            </pre>
          </div>
        </Drawer>
      ) : null}
    </div>
  );
}
